// ===========================================
// Xero Integration Tab
// Embedded Xero settings and connection management
// ===========================================

import { usePermissions } from '../../../hooks/usePermissions';
import { useOwnerTitle } from '../../../hooks/useOwnerTitle';
import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Typography,
  Button,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Switch,
  Chip,
  Alert,
  AlertTitle,
  CircularProgress,
  Skeleton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stack,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import {
  AccountBalance as AccountingIcon,
  Sync as SyncIcon,
  Check as CheckIcon,
  Link as LinkIcon,
  LinkOff as LinkOffIcon,
  Settings as SettingsIcon,
  Receipt as InvoiceIcon,
  People as ContactIcon,
} from '@mui/icons-material';
import {
  useXeroStatus,
  useXeroAuthUrl,
  useDisconnectXero,
  useXeroAccounts,
  useXeroBankAccounts,
  useUpdateXeroSettings,
  useUpdateAccountMappings,
  useTriggerXeroSync,
  useXeroSyncStats,
} from '../../../hooks/useXero';
import {
  useIntegrationSettings,
  useUpdateIntegrationSettings,
} from '../../../hooks/useIntegrations';
import { formatSyncTime, XeroAccount, XeroBankAccount } from '../../../api/xero.api';

// ===========================================
// MAIN COMPONENT
// ===========================================

export default function XeroIntegrationTab() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { isOwner, can } = usePermissions();
  const ownerTitle = useOwnerTitle();
  // Xero's settings, mappings and sync need integrations AND invoices write (roles plan 5.6).
  const canEditXero = can('integrations', 'write') && can('operations_invoices', 'write');
  const [showDisconnectDialog, setShowDisconnectDialog] = useState(false);
  const [showAccountMappingDialog, setShowAccountMappingDialog] = useState(false);

  // Account mapping form state
  const [salesAccountCode, setSalesAccountCode] = useState<string>('');
  const [bankAccountId, setBankAccountId] = useState<string>('');

  // Integration settings
  const { data: integrationSettings } = useIntegrationSettings();
  const updateIntegrationSettings = useUpdateIntegrationSettings();
  const xeroSettings = integrationSettings?.xero;

  // Xero API hooks
  const { data: status, isLoading: statusLoading, refetch: refetchStatus } = useXeroStatus();
  const { data: accounts, isLoading: accountsLoading, refetch: fetchAccounts } = useXeroAccounts();
  const { data: bankAccounts, isLoading: bankAccountsLoading, refetch: fetchBankAccounts } = useXeroBankAccounts();
  const { data: syncStats, isLoading: syncStatsLoading } = useXeroSyncStats();

  const authUrlMutation = useXeroAuthUrl();
  const disconnectMutation = useDisconnectXero();
  const updateSettingsMutation = useUpdateXeroSettings();
  const updateAccountMappingsMutation = useUpdateAccountMappings();
  const syncMutation = useTriggerXeroSync();

  // Handle OAuth callback results from URL params
  useEffect(() => {
    const connected = searchParams.get('connected');
    const error = searchParams.get('error');

    if (connected === 'true') {
      refetchStatus();
      setSearchParams({});
    } else if (error) {
      setSearchParams({});
    }
  }, [searchParams, setSearchParams, refetchStatus]);

  // Update form state when status loads
  useEffect(() => {
    if (status?.accountMappings) {
      setSalesAccountCode(status.accountMappings.salesAccountCode || '');
      setBankAccountId(status.accountMappings.bankAccountId || '');
    }
  }, [status]);

  // ===========================================
  // HANDLERS
  // ===========================================

  const handleToggleEnabled = async () => {
    if (!xeroSettings) return;
    await updateIntegrationSettings.mutateAsync({
      xero: { enabled: !xeroSettings.enabled },
    });
  };

  const handleConnect = async () => {
    const result = await authUrlMutation.mutateAsync();
    if (result.authUrl) {
      window.location.href = result.authUrl;
    }
  };

  const handleDisconnect = async () => {
    await disconnectMutation.mutateAsync();
    setShowDisconnectDialog(false);
  };

  const handleToggleSyncEnabled = async () => {
    await updateSettingsMutation.mutateAsync({
      syncEnabled: !status?.syncEnabled,
    });
  };

  const handleToggleSyncInvoices = async () => {
    await updateSettingsMutation.mutateAsync({
      syncInvoices: !status?.syncInvoices,
    });
  };

  const handleToggleSyncPayments = async () => {
    await updateSettingsMutation.mutateAsync({
      syncPayments: !status?.syncPayments,
    });
  };

  const handleToggleAutoCreateContacts = async () => {
    await updateSettingsMutation.mutateAsync({
      autoCreateContacts: !status?.autoCreateContacts,
    });
  };

  const handleOpenAccountMappings = async () => {
    await Promise.all([fetchAccounts(), fetchBankAccounts()]);
    setShowAccountMappingDialog(true);
  };

  const handleSaveAccountMappings = async () => {
    await updateAccountMappingsMutation.mutateAsync({
      salesAccountCode: salesAccountCode || null,
      bankAccountId: bankAccountId || null,
    });
    setShowAccountMappingDialog(false);
  };

  const handleManualSync = async () => {
    await syncMutation.mutateAsync({ syncType: 'full' });
  };

  // ===========================================
  // LOADING STATE
  // ===========================================

  if (statusLoading) {
    return <Skeleton variant="rectangular" height={400} />;
  }

  return (
    <Box>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Connect Xero to automatically sync invoices, payments, and contacts to your accounting
        software.
      </Typography>

      {searchParams.get('error') && (
        <Alert severity="error" sx={{ mb: 3 }}>
          <AlertTitle>Connection Failed</AlertTitle>
          {searchParams.get('error') === 'auth_failed'
            ? 'Failed to authorize with Xero. Please try again.'
            : 'An error occurred during authorization.'}
        </Alert>
      )}

      {searchParams.get('connected') && (
        <Alert severity="success" sx={{ mb: 3 }}>
          <AlertTitle>Connected Successfully</AlertTitle>
          Your Xero account is now connected. Configure your account mappings below.
        </Alert>
      )}

      {/* Master Enable/Disable */}
      <Card sx={{ mb: 3 }}>
        <CardHeader
          avatar={<AccountingIcon color={xeroSettings?.enabled ? 'primary' : 'disabled'} />}
          title="Xero Integration"
          action={
            <Switch
              checked={xeroSettings?.enabled ?? false}
              onChange={handleToggleEnabled}
              disabled={updateIntegrationSettings.isPending || !canEditXero || !can('system_settings', 'write') || !isOwner}
            />
          }
        />
        <CardContent>
          <Typography variant="body2" color="text.secondary">
            {isOwner
              ? 'Enable or disable Xero integration for your school. When disabled, no syncing will occur.'
              : `Only your ${ownerTitle} can turn Xero on or off.`}
          </Typography>
        </CardContent>
      </Card>

      {/* Connection Card */}
      {xeroSettings?.enabled && (
        <Card sx={{ mb: 3 }}>
          <CardHeader
            title={status?.isConnected ? 'Xero Connected' : 'Connect to Xero'}
            subheader={
              status?.isConnected
                ? `Organization: ${status.tenantName || 'Unknown'}`
                : 'Connect your Xero account to start syncing'
            }
            action={
              status?.isConnected && (
                <Chip icon={<CheckIcon />} label="Connected" color="success" size="small" />
              )
            }
          />
          <CardContent>
            {!status?.isConnected ? (
              <>
                <Typography variant="body2" sx={{ mb: 3 }}>
                  Connect your Xero accounting software to automatically sync invoices and payments.
                </Typography>
                <List dense>
                  <ListItem>
                    <ListItemIcon>
                      <CheckIcon color="success" fontSize="small" />
                    </ListItemIcon>
                    <ListItemText primary="Automatic invoice sync to Xero" />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <CheckIcon color="success" fontSize="small" />
                    </ListItemIcon>
                    <ListItemText primary="Payment recording when invoices are paid" />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <CheckIcon color="success" fontSize="small" />
                    </ListItemIcon>
                    <ListItemText primary="Automatic contact creation for families" />
                  </ListItem>
                </List>
                {/* Connecting the accounting link is the owner's act (roles plan §13 item 2). */}
                {isOwner ? (
                  <Button
                    variant="contained"
                    startIcon={<LinkIcon />}
                    onClick={handleConnect}
                    disabled={authUrlMutation.isPending}
                    sx={{ mt: 2 }}
                  >
                    {authUrlMutation.isPending ? 'Connecting...' : 'Connect to Xero'}
                  </Button>
                ) : (
                  <Alert severity="info" sx={{ mt: 2 }}>Only your {ownerTitle} can connect the school's Xero account.</Alert>
                )}
              </>
            ) : (
              <Stack spacing={2}>
                {/* Sync Toggle */}
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography variant="subtitle2">Automatic Sync</Typography>
                    <Typography variant="body2" color="text.secondary">
                      Enable automatic syncing of invoices and payments
                    </Typography>
                  </Box>
                  <Switch
                    checked={status.syncEnabled}
                    onChange={handleToggleSyncEnabled}
                    disabled={updateSettingsMutation.isPending || !canEditXero}
                  />
                </Box>

                <Divider />

                {/* Sync Invoices */}
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography variant="subtitle2">Sync Invoices</Typography>
                    <Typography variant="body2" color="text.secondary">
                      Automatically sync invoices to Xero
                    </Typography>
                  </Box>
                  <Switch
                    checked={status.syncInvoices}
                    onChange={handleToggleSyncInvoices}
                    disabled={!status.syncEnabled || updateSettingsMutation.isPending || !canEditXero}
                  />
                </Box>

                {/* Sync Payments */}
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography variant="subtitle2">Sync Payments</Typography>
                    <Typography variant="body2" color="text.secondary">
                      Record payments in Xero when invoices are paid
                    </Typography>
                  </Box>
                  <Switch
                    checked={status.syncPayments}
                    onChange={handleToggleSyncPayments}
                    disabled={!status.syncEnabled || updateSettingsMutation.isPending || !canEditXero}
                  />
                </Box>

                {/* Auto Create Contacts */}
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography variant="subtitle2">Auto-Create Contacts</Typography>
                    <Typography variant="body2" color="text.secondary">
                      Automatically create contacts in Xero for families
                    </Typography>
                  </Box>
                  <Switch
                    checked={status.autoCreateContacts}
                    onChange={handleToggleAutoCreateContacts}
                    disabled={!status.syncEnabled || updateSettingsMutation.isPending || !canEditXero}
                  />
                </Box>

                <Divider />

                {/* Account Mappings */}
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography variant="subtitle2">Account Mappings</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {status.accountMappings.salesAccountCode
                        ? `Sales: ${status.accountMappings.salesAccountCode}`
                        : 'Not configured'}
                    </Typography>
                  </Box>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<SettingsIcon />}
                    onClick={handleOpenAccountMappings}
                    disabled={!canEditXero}
                  >
                    Configure
                  </Button>
                </Box>

                <Divider />

                {/* Sync Stats */}
                {!syncStatsLoading && syncStats && (
                  <Box>
                    <Typography variant="subtitle2" gutterBottom>
                      Sync Status
                    </Typography>
                    <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
                      <Chip
                        icon={<ContactIcon />}
                        label={`${syncStats.contacts.synced}/${syncStats.contacts.total} contacts`}
                        size="small"
                        color={syncStats.contacts.errors > 0 ? 'warning' : 'success'}
                        variant="outlined"
                      />
                      <Chip
                        icon={<InvoiceIcon />}
                        label={`${syncStats.invoices.synced}/${syncStats.invoices.total} invoices`}
                        size="small"
                        color={syncStats.invoices.errors > 0 ? 'warning' : 'success'}
                        variant="outlined"
                      />
                    </Stack>
                  </Box>
                )}

                <Divider />

                {/* Last Sync & Actions */}
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography variant="subtitle2">Last Sync</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {formatSyncTime(status.lastSyncAt)}
                    </Typography>
                  </Box>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={syncMutation.isPending ? <CircularProgress size={16} /> : <SyncIcon />}
                    onClick={handleManualSync}
                    disabled={syncMutation.isPending || !canEditXero}
                  >
                    Sync Now
                  </Button>
                </Box>

                <Divider />

                {/* Disconnect: owner only (roles plan §13 item 2) */}
                {isOwner && (
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <Button
                      variant="text"
                      color="error"
                      startIcon={<LinkOffIcon />}
                      onClick={() => setShowDisconnectDialog(true)}
                    >
                      Disconnect
                    </Button>
                  </Box>
                )}
              </Stack>
            )}
          </CardContent>
        </Card>
      )}

      {/* Account Mapping Dialog */}
      <Dialog
        open={showAccountMappingDialog}
        onClose={() => setShowAccountMappingDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Configure Account Mappings</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Map your Music &apos;n Me accounts to Xero accounts for accurate bookkeeping.
          </Typography>

          {accountsLoading || bankAccountsLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <Stack spacing={3}>
              <FormControl fullWidth>
                <InputLabel>Sales Account</InputLabel>
                <Select
                  value={salesAccountCode}
                  onChange={(e) => setSalesAccountCode(e.target.value)}
                  label="Sales Account"
                >
                  <MenuItem value="">
                    <em>None</em>
                  </MenuItem>
                  {accounts
                    ?.filter((a: XeroAccount) => a.type === 'REVENUE' && a.status === 'ACTIVE')
                    .map((account: XeroAccount) => (
                      <MenuItem key={account.accountId} value={account.code}>
                        {account.code} - {account.name}
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>

              <FormControl fullWidth>
                <InputLabel>Bank Account (for payments)</InputLabel>
                <Select
                  value={bankAccountId}
                  onChange={(e) => setBankAccountId(e.target.value)}
                  label="Bank Account (for payments)"
                >
                  <MenuItem value="">
                    <em>None</em>
                  </MenuItem>
                  {bankAccounts?.map((account: XeroBankAccount) => (
                    <MenuItem key={account.accountId} value={account.accountId}>
                      {account.code} - {account.name} ({account.currencyCode})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowAccountMappingDialog(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSaveAccountMappings}
            disabled={updateAccountMappingsMutation.isPending}
          >
            {updateAccountMappingsMutation.isPending ? 'Saving...' : 'Save Mappings'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Disconnect Confirmation Dialog */}
      <Dialog
        open={showDisconnectDialog}
        onClose={() => setShowDisconnectDialog(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Disconnect Xero?</DialogTitle>
        <DialogContent>
          <Typography>
            This will remove the connection to your Xero account. Synced invoices and contacts will
            remain in Xero but will no longer be updated automatically.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowDisconnectDialog(false)}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDisconnect}
            disabled={disconnectMutation.isPending}
          >
            {disconnectMutation.isPending ? 'Disconnecting...' : 'Disconnect'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
